import React, { useEffect, useRef, useState } from 'react';

import { useGround } from '../hooks/useGround';

/**
 * TerrainField — the live contour field, ported from the public site's
 * `TerrainField.astro` (Natshoot.Public/src/components/TerrainField.astro).
 * Same shader, same palette tokens, same degradation ladder — this is the one
 * WebGL surface the hub uses, kept a straight port rather than a reinvention
 * so the two surfaces stay one thing rather than two animations that happen
 * to look similar.
 *
 * The brand's topographic pattern, made live. A contour field is rendered
 * from a noise height-map in a fragment shader; the pointer deforms the
 * height-map under it, so the contours genuinely re-draw around the cursor
 * rather than having a highlight pasted over them. Contours inside the
 * cursor's radius are picked out in Apricot — the one accent this surface
 * spends, matching the Fill-Not-Ink Rule everywhere else in the hub.
 *
 * Written against raw WebGL2 on purpose, same as the Astro original: the
 * team's standing rule is to build UI in-house rather than adopt libraries,
 * and no animation engine is approved for this module either.
 *
 * Degradation, in order:
 *   no WebGL         → the CSS ground stays, with a static SVG contour plate
 *   reduced motion    → no autonomous drift; the pointer still deforms the
 *                       field, because that is a direct response to input
 *   coarse pointer    → touch drives the deformation directly; the field
 *                       also drifts on its own between touches
 *
 * Palette comes from `useGround()` rather than a MutationObserver on
 * `data-ground` (the Astro version's approach) — the hub already has a
 * live store for the ground, so re-reading the CSS custom properties on
 * every `ground` change is the React-native way to stay in step with it.
 */

const VERT = `#version 300 es
in vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }`;

// The height-map is value-noise fbm. Contours come from banding that field and
// measuring each fragment's distance to the nearest band edge in screen space,
// which is what keeps every line one pixel wide no matter how steep the slope.
const FRAG = `#version 300 es
precision highp float;

uniform vec2  uRes;
uniform float uTime;
uniform vec2  uMouse;      // aspect-corrected, same space as p
uniform float uMouseOn;    // 0..1, eases in on pointer enter
uniform float uDrift;      // 0 when the visitor asked for reduced motion

uniform vec3 uGroundLow;
uniform vec3 uGroundHigh;
uniform vec3 uLine;
uniform vec3 uLineHi;
uniform vec3 uAccent;
uniform vec3  uVignette;   // the colour the field settles toward at the edge
uniform float uVignetteAmt;

out vec4 outColor;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  mat2 rot = mat2(0.8, -0.6, 0.6, 0.8);
  for (int i = 0; i < 5; i++) {
    v += amp * valueNoise(p);
    p = rot * p * 2.02;
    amp *= 0.5;
  }
  return v;
}

void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;

  // Slow survey drift, so the field breathes when nobody is touching it.
  vec2 q = p * 1.75 + vec2(uTime * 0.011, uTime * 0.0072) * uDrift;
  float h = fbm(q);

  // The pointer raises a smooth hill in the height-map. Because the contours
  // are derived from h *after* this, they bend around the cursor for real.
  float d = distance(p, uMouse);
  float bump = exp(-(d * d) / 0.012);
  h += uMouseOn * 0.20 * bump;

  // Contour banding.
  const float BANDS = 30.0;
  float f = h * BANDS;
  float dist = abs(fract(f - 0.5) - 0.5) / max(fwidth(f), 1e-5);
  float line = 1.0 - min(dist, 1.0);

  // Ground reads slightly lighter on high ground — tonal depth, no lighting model.
  vec3 ground = mix(uGroundLow, uGroundHigh, smoothstep(0.25, 0.85, h));

  // Ridge lines lift toward steel so the field has depth and can carry a
  // viewport; valley lines stay on the brand's own motif navy.
  vec3 lineCol = mix(uLine, uLineHi, smoothstep(0.28, 0.86, h));

  // Apricot only where the pointer is. This is the one accent on the surface.
  float near = uMouseOn * smoothstep(0.26, 0.02, d);
  lineCol = mix(lineCol, uAccent, near);

  // Lines under the cursor also gain weight, so the highlight reads as focus
  // rather than as a colour swap.
  float strength = 0.85 + 0.15 * near;

  vec3 col = mix(ground, lineCol, line * strength);

  // Faint vignette to seat the type. Blended toward a ground-supplied colour,
  // never multiplied: multiplying is a dark-ground assumption, and on the
  // paper sheet it turns warm white grey.
  col = mix(col, uVignette, uVignetteAmt * smoothstep(0.35, 1.15, length(p * vec2(0.85, 1.0))));

  outColor = vec4(col, 1.0);
}`;

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '').trim();
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
};

const compile = (gl: WebGL2RenderingContext, type: number, src: string): WebGLShader | null => {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.warn('[terrain] shader failed:', gl.getShaderInfoLog(sh));
    gl.deleteShader(sh);
    return null;
  }
  return sh;
};

/** The four registration ticks, and the survey-sheet contour plate shown before WebGL initialises. */
const Fallback: React.FC = () => (
  <svg
    className="pointer-events-none absolute inset-0 h-full w-full"
    aria-hidden="true"
    viewBox="0 0 1200 700"
    preserveAspectRatio="xMidYMid slice"
  >
    <g fill="none" stroke="var(--terrain-line)" strokeWidth={1.1}>
      {Array.from({ length: 16 }).map((_, i) => {
        const k = i / 15;
        const amp = 26 + i * 5;
        const y = 90 + i * 34;
        const d = Array.from({ length: 41 })
          .map((__, j) => {
            const x = (j / 40) * 1200;
            const yy =
              y +
              Math.sin(j / 5 + i * 0.7) * amp * 0.5 +
              Math.sin(j / 11 - i * 0.4) * amp * 0.35;
            return `${j === 0 ? 'M' : 'L'}${x.toFixed(1)} ${yy.toFixed(1)}`;
          })
          .join(' ');
        return <path key={i} d={d} opacity={0.35 + k * 0.3} />;
      })}
    </g>
  </svg>
);

interface TerrainFieldProps {
  className?: string;
}

const TerrainField: React.FC<TerrainFieldProps> = ({ className = '' }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const { ground } = useGround();

  // Re-reads the palette on every ground flip rather than tearing the whole
  // WebGL context down and rebuilding it — the uniforms are the only thing
  // that changes, so this is a ref the init effect below can call into.
  const applyPaletteRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    applyPaletteRef.current?.();
  }, [ground]);

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;

    const gl = canvas.getContext('webgl2', {
      antialias: false,
      alpha: false,
      powerPreference: 'low-power',
      // Without this the drawing buffer is cleared after every composite, so any
      // frame where rAF is throttled — a background tab, a paused observer, a
      // screenshot — paints blank. Costs a copy; buys a field that is never empty.
      preserveDrawingBuffer: true,
    });
    if (!gl) return; // SVG plate stays visible

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    const prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('[terrain] link failed:', gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);

    // Fullscreen triangle — cheaper than a quad and has no diagonal seam.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const u = (n: string): WebGLUniformLocation | null => gl.getUniformLocation(prog, n);
    const uRes = u('uRes');
    const uTime = u('uTime');
    const uMouse = u('uMouse');
    const uMouseOn = u('uMouseOn');
    const uDrift = u('uDrift');

    // Colours come from the ground tokens, so the field follows whichever
    // ground the hub commits to without the shader knowing anything about it.
    // Values may be hex or rgba() — a translucent line token is how the paper
    // ground gets contours that sit *in* the sheet rather than on top of it.
    function readColor(name: string, fallback: string): [number, number, number] {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      const v = raw || fallback;
      if (v.startsWith('#')) return hexToRgb(v);
      const m = v.match(/rgba?\(([^)]+)\)/);
      if (!m) return hexToRgb(fallback);
      const parts = m[1].split(',').map((s) => parseFloat(s));
      const [r, g, b, a = 1] = parts;
      // Composite against the ground so the shader stays fully opaque.
      const lowRaw = getComputedStyle(document.documentElement).getPropertyValue('--terrain-low').trim();
      const bg = hexToRgb(lowRaw.startsWith('#') ? lowRaw : '#ffffff');
      return [
        (r / 255) * a + bg[0] * (1 - a),
        (g / 255) * a + bg[1] * (1 - a),
        (b / 255) * a + bg[2] * (1 - a),
      ];
    }

    function applyPalette(): void {
      if (!gl || !prog) return;
      gl.useProgram(prog);
      gl.uniform3fv(u('uGroundLow'), readColor('--terrain-low', '#03081f'));
      gl.uniform3fv(u('uGroundHigh'), readColor('--terrain-high', '#061139'));
      gl.uniform3fv(u('uLine'), readColor('--terrain-line', '#172956'));
      gl.uniform3fv(u('uLineHi'), readColor('--terrain-line-hi', '#3c5490'));
      gl.uniform3fv(u('uAccent'), readColor('--apricot', '#f1891d'));
      gl.uniform3fv(u('uVignette'), readColor('--terrain-vignette', '#01040e'));
      gl.uniform1f(
        u('uVignetteAmt'),
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--terrain-vignette-amt').trim(),
        ) || 0.42,
      );
    }
    applyPalette();
    applyPaletteRef.current = applyPalette;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarse = window.matchMedia('(pointer: coarse)');

    let dpr = 1;
    function resize(): void {
      if (!gl) return;
      // Cap DPR: the shader is fill-rate bound and a 3x retina phone gains
      // nothing visible from the extra pixels.
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(root!.clientWidth * dpr));
      const h = Math.max(1, Math.round(root!.clientHeight * dpr));
      if (canvas!.width !== w || canvas!.height !== h) {
        canvas!.width = w;
        canvas!.height = h;
        gl.viewport(0, 0, w, h);
      }
    }

    // Pointer, in the same aspect-corrected space the shader uses.
    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    let targetOn = 0;
    let curOn = 0;

    function toField(clientX: number, clientY: number): readonly [number, number] {
      const r = root!.getBoundingClientRect();
      const x = (clientX - r.left - r.width / 2) / r.height;
      const y = -(clientY - r.top - r.height / 2) / r.height;
      return [x, y] as const;
    }

    const cleanups: Array<() => void> = [];
    let releaseTimer = 0;

    if (!coarse.matches) {
      const onMove = (e: PointerEvent): void => {
        const [x, y] = toField(e.clientX, e.clientY);
        targetX = x;
        targetY = y;
        targetOn = 1;
      };
      const onLeave = (): void => {
        targetOn = 0;
      };
      root.addEventListener('pointermove', onMove);
      root.addEventListener('pointerleave', onLeave);
      cleanups.push(() => root.removeEventListener('pointermove', onMove));
      cleanups.push(() => root.removeEventListener('pointerleave', onLeave));
    } else {
      // A touch device has no hover, but it does have a finger — and the pinned
      // interaction is the point of this surface, so it is authored for touch
      // rather than degraded away. Every listener is passive and nothing calls
      // preventDefault, so a drag scrolls the page *and* deforms the field under
      // the finger; touchmove keeps firing through a scroll gesture, which
      // pointermove does not once the browser takes the gesture over.
      const track = (t: Touch): void => {
        const [x, y] = toField(t.clientX, t.clientY);
        targetX = x;
        targetY = y;
        targetOn = 1;
        window.clearTimeout(releaseTimer);
      };
      const onStart = (e: TouchEvent): void => track(e.touches[0]);
      const onTouchMove = (e: TouchEvent): void => track(e.touches[0]);
      const lift = (): void => {
        // Hold the raised ground briefly after the finger leaves, then let it
        // settle. Dropping it on touchend reads as a glitch, not a release.
        releaseTimer = window.setTimeout(() => {
          targetOn = 0;
        }, 900);
      };
      root.addEventListener('touchstart', onStart, { passive: true });
      root.addEventListener('touchmove', onTouchMove, { passive: true });
      root.addEventListener('touchend', lift, { passive: true });
      root.addEventListener('touchcancel', lift, { passive: true });
      cleanups.push(() => root.removeEventListener('touchstart', onStart));
      cleanups.push(() => root.removeEventListener('touchmove', onTouchMove));
      cleanups.push(() => root.removeEventListener('touchend', lift));
      cleanups.push(() => root.removeEventListener('touchcancel', lift));
      cleanups.push(() => window.clearTimeout(releaseTimer));
    }

    let raf = 0;
    const start = performance.now();

    function frame(now: number): void {
      if (!gl) return;
      resize();
      const t = (now - start) / 1000;

      // Ease the pointer so the deformation feels like weight, not a jump.
      const k = 0.12;
      curX += (targetX - curX) * k;
      curY += (targetY - curY) * k;
      curOn += (targetOn - curOn) * 0.08;

      gl.uniform2f(uRes, canvas!.width, canvas!.height);
      gl.uniform1f(uTime, t);
      gl.uniform2f(uMouse, curX, curY);
      gl.uniform1f(uMouseOn, curOn);
      gl.uniform1f(uDrift, reduced.matches ? 0 : 1);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      raf = requestAnimationFrame(frame);
    }

    // Don't burn a GPU on a hero that has scrolled away.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !raf) raf = requestAnimationFrame(frame);
        if (!entry.isIntersecting && raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      },
      { threshold: 0.01 },
    );
    io.observe(root);

    resize();
    raf = requestAnimationFrame(frame);
    setReady(true);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      cleanups.forEach((fn) => fn());
      applyPaletteRef.current = null;
      // Deliberately not calling `WEBGL_lose_context`'s `loseContext()` here.
      // `canvas.getContext()` returns the *same* context object on a second call
      // against the same node rather than creating a fresh one — which is exactly
      // what happens when React's dev StrictMode runs this effect, its cleanup,
      // and then the effect again, all against the one DOM node. Explicitly
      // losing the context on that first cleanup left the second, real mount
      // compiling shaders against an already-dead context (every compile failed
      // silently). A normal unmount removes the canvas node entirely — Route
      // navigation back to this page mounts a brand-new node and a fresh
      // context there is created for free, so there is nothing this needs to
      // release by hand.
    };
  }, []);

  // `position` and sizing are the caller's call, not this component's — a
  // hardcoded `relative` here previously fought a caller-supplied `absolute
  // inset-0`. Tailwind's own utility order settles ties between two classes
  // for the same property by source order, not by which one is "more
  // specific" or was written last, so `relative` silently won and the whole
  // layer collapsed to zero height (confirmed: canvas measured 1316×1).
  // `overflow-hidden` and the resting ground colour are the only opinions
  // this component holds; everything about where it sits is `className`.
  return (
    <div ref={rootRef} className={`overflow-hidden bg-[var(--terrain-low)] ${className}`}>
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 900ms var(--ease)' }}
      />
      <Fallback />
    </div>
  );
};

export default TerrainField;
